/**
 * The public certificate page, where anybody holding a certificate id checks
 * it (docs/certificates.md). Names and the claim come from ./certificate, so
 * nothing here restates them.
 *
 * Written for strings/person.ts's reader, in its register: a third party who
 * was sent a link, has never heard of this product, and is deciding whether to
 * believe the person who sent it. So the page speaks about the holder rather
 * than to them, and every sentence has one job — to say what EngineerOS is
 * vouching for and where that stops.
 *
 * ## The rule this file is written to
 *
 * The certificate may claim exactly one sentence (`certificate.claim`) and
 * nothing wider. Everything below either restates a part of that sentence
 * (`how.server`, `how.own`), says where it stops (`how.limit`,
 * `provenance`), or reports a fact the record holds (dates, the id, whether it
 * was withdrawn). A line that says something about the holder the grader did
 * not measure — a skill, a level, a grade, an hour count — is a change to the
 * contract, not to this file.
 *
 * ## A withdrawn certificate
 *
 * Its page stays, because a link somebody already shared must not turn into
 * "never existed", and it must not keep vouching either (docs/certificates.md,
 * "Revocation"). So `revoked.body` withdraws the checkpoints along with the
 * certificate, and the page stops listing them. It does not say why: the
 * public record carries no reason, and a guess on a page about a named person
 * is worse than silence.
 */

export const en = {
  /**
   * The tab's title, and until the Worker renders this document per id, the
   * only page metadata a shared link carries beyond the site-wide card in
   * index.html (strings/person.ts `tab` makes the same trade). The holder
   * first, because that is who the reader is checking.
   */
  tab: (holder: string, name: string) => `${holder} · ${name} certificate · EngineerOS`,
  tabRevoked: (holder: string, name: string) => `${holder} · ${name} certificate, no longer valid · EngineerOS`,
  tabMissing: 'No such certificate · EngineerOS',

  label: 'Certificate',
  loading: 'Checking…',
  issuedTo: 'Issued to',
  issued: 'Issued',

  /**
   * The state, which is the first thing a checking reader wants and so the
   * first thing on the page. Two words each, for a reader who stops there.
   */
  valid: 'Valid',
  validBody: 'EngineerOS issued this certificate and has not withdrawn it.',
  revoked: {
    state: 'No longer valid',
    since: (date: string) => `No longer valid since ${date}`,
    body: 'EngineerOS withdrew this certificate. It no longer vouches for it, or for the checkpoints it was issued on. The page stays so that a link already shared says so rather than disappearing.',
  },

  checkpoints: {
    label: 'The checkpoints behind it',
    on: (date: string) => `Passed ${date}`,
    /* Before the grading version, which is set as machine text beside it. */
    graded: 'graded under',
  },

  /**
   * The claim, taken apart into the three things a stranger needs from it.
   *
   * The first two restate halves of `certificate.claim`, and the third is its
   * limit, said as plainly: the salt stops a finished workspace being copied
   * from another account and cannot stop somebody else doing the work in this
   * one (docs/cheating.md). Nothing here adds to what the grader measured.
   */
  how: {
    label: 'How to read it',
    server: {
      term: 'Graded by a server',
      body: 'Each checkpoint was graded on EngineerOS’s servers, on the repository that was submitted — not by the holder’s own browser, which can report nothing that passes one.',
    },
    own: {
      term: 'A repository of their own',
      body: 'Each checkpoint started from a repository issued to this account and no other, so a finished workspace copied from another account does not pass.',
    },
    limit: {
      term: 'What it does not say',
      body: 'It says which account passed, not who was at the keyboard. On EngineerOS an agent may do the typing while the holder directs and judges. It carries no grade, score or hour count, because the grader measured none.',
    },
  },

  id: {
    label: 'Certificate id',
    body: 'Anybody given this id can open this page, and sees what is true of the certificate now.',
  },

  profile: {
    label: 'Public record',
    link: 'See the holder’s public record',
    body: 'Every checkpoint EngineerOS’s server has confirmed for this account, as it stands today.',
  },

  /* Who is vouching, said plainly, as strings/person.ts `provenance` does. */
  provenance:
    'EngineerOS issues this certificate from its own grading server’s records. No other body has reviewed or accredited it.',

  /**
   * An id that answers nothing. One sentence for a mistyped id and for an
   * account that was deleted, because the Worker does not tell them apart and
   * neither should the page (docs/certificates.md, "Revocation").
   */
  missing: {
    title: 'No such certificate',
    body: 'EngineerOS has no certificate with this id. It may have been mistyped, or the account that held it may have been closed.',
    asked: 'The id you opened',
  },

  cta: 'See the course',
  ctaWhy:
    'EngineerOS teaches software engineering by directing AI agents, on a real Linux machine inside the browser tab. Its checkpoints are graded on a server, and its certificates are issued on those grades.',
};

export const ar: typeof en = {
  tab: (holder: string, name: string) => `${holder} · شهادة ${name} · EngineerOS`,
  tabRevoked: (holder: string, name: string) => `${holder} · شهادة ${name}، لم تعد صالحة · EngineerOS`,
  tabMissing: 'لا توجد شهادة كهذه · EngineerOS',

  label: 'شهادة',
  loading: 'جارٍ التحقّق…',
  issuedTo: 'صدرت باسم',
  issued: 'تاريخ الإصدار',

  valid: 'صالحة',
  validBody: 'أصدر EngineerOS هذه الشهادة ولم يسحبها.',
  revoked: {
    state: 'لم تعد صالحة',
    since: (date: string) => `لم تعد صالحة منذ ${date}`,
    body: 'سحب EngineerOS هذه الشهادة، فلم يعد يشهد لها ولا للمحطات التي صدرت بناءً عليها. وتبقى الصفحة حتى يقول الرابط الذي شورك من قبلُ ذلك بدل أن يختفي.',
  },

  checkpoints: {
    label: 'المحطات التي تقوم عليها',
    on: (date: string) => `اجتيزت في ${date}`,
    graded: 'قُيّمت وفق',
  },

  how: {
    label: 'كيف تُقرأ',
    server: {
      term: 'قيّمها خادم',
      body: 'قُيّمت كل محطة على خوادم EngineerOS، على المستودع الذي أُرسل — لا في متصفّح صاحب الشهادة، الذي لا يستطيع أن يبلّغ عن شيء يجتاز محطة.',
    },
    own: {
      term: 'مستودع خاص بالحساب',
      body: 'بدأت كل محطة من مستودع صدر لهذا الحساب وحده، فمساحة عمل منتهية منسوخة من حساب آخر لا تجتازها.',
    },
    limit: {
      term: 'ما لا تقوله',
      body: 'تقول أيّ حساب اجتاز، لا من كان على لوحة المفاتيح. ففي EngineerOS قد يكتب وكيلٌ الأوامر بينما يوجّهه صاحب الشهادة ويحكم على عمله. ولا تحمل درجة ولا علامة ولا عدد ساعات، لأن المُقيِّم لم يقِس شيئًا من ذلك.',
    },
  },

  id: {
    label: 'رقم الشهادة',
    body: 'يستطيع أيّ شخص يُعطى هذا الرقم أن يفتح هذه الصفحة، فيرى ما يصحّ عن الشهادة الآن.',
  },

  profile: {
    label: 'السجلّ العلني',
    link: 'اطّلع على سجلّ صاحب الشهادة العلني',
    body: 'كل محطة أكّدها خادم EngineerOS لهذا الحساب، كما هي اليوم.',
  },

  provenance: 'يُصدر EngineerOS هذه الشهادة من سجلّات خادم التقييم الخاص به. ولم تراجعها أو تعتمدها أيّ جهة أخرى.',

  missing: {
    title: 'لا توجد شهادة كهذه',
    body: 'ليس لدى EngineerOS شهادة بهذا الرقم. قد يكون في كتابته خطأ، أو يكون الحساب الذي حملها قد أُغلق.',
    asked: 'الرقم الذي فتحته',
  },

  cta: 'اطّلع على المسار',
  ctaWhy:
    '‏EngineerOS يعلّم هندسة البرمجيات عبر توجيه وكلاء الذكاء الاصطناعي، على جهاز لينكس حقيقي داخل تبويب المتصفّح. تُقيَّم محطاته على خادم، وتصدر شهاداته بناءً على ذلك التقييم.',
};

export const de: typeof en = {
  tab: (holder, name) => `${holder} · Zertifikat ${name} · EngineerOS`,
  tabRevoked: (holder, name) => `${holder} · Zertifikat ${name}, nicht mehr gültig · EngineerOS`,
  tabMissing: 'Kein solches Zertifikat · EngineerOS',
  label: 'Zertifikat',
  loading: 'Wird geprüft …',
  issuedTo: 'Ausgestellt für',
  issued: 'Ausgestellt',
  valid: 'Gültig',
  validBody: 'EngineerOS hat dieses Zertifikat ausgestellt und nicht zurückgezogen.',
  revoked: {
    state: 'Nicht mehr gültig',
    since: (date) => `Nicht mehr gültig seit ${date}`,
    body: 'EngineerOS hat dieses Zertifikat zurückgezogen. Es bürgt nicht mehr dafür, auch nicht für die Checkpoints, auf denen es beruhte. Die Seite bleibt bestehen, damit ein bereits geteilter Link das sagt, statt einfach zu verschwinden.',
  },
  checkpoints: {
    label: 'Die Checkpoints dahinter',
    on: (date) => `Bestanden am ${date}`,
    graded: 'bewertet nach',
  },
  how: {
    label: 'Wie man es liest',
    server: {
      term: 'Von einem Server bewertet',
      body: 'Jeder Checkpoint wurde auf den Servern von EngineerOS bewertet, anhand des eingereichten Repositorys – nicht im Browser der Person, die es hält; der kann nichts melden, was einen Checkpoint besteht.',
    },
    own: {
      term: 'Ein eigenes Repository',
      body: 'Jeder Checkpoint begann mit einem Repository, das nur für dieses Konto ausgestellt wurde. Ein fertiger Arbeitsstand, der von einem anderen Konto kopiert wurde, besteht nicht.',
    },
    limit: {
      term: 'Was es nicht sagt',
      body: 'Es sagt, welches Konto bestanden hat, nicht, wer an der Tastatur saß. Bei EngineerOS darf ein Agent tippen, während die Inhaberin oder der Inhaber anleitet und beurteilt. Es enthält keine Note, keine Punktzahl und keine Stundenzahl, weil die Bewertung nichts davon gemessen hat.',
    },
  },
  id: {
    label: 'Zertifikats-ID',
    body: 'Wer diese ID bekommt, kann diese Seite öffnen und sieht, was jetzt für das Zertifikat gilt.',
  },
  profile: {
    label: 'Öffentlicher Nachweis',
    link: 'Öffentlichen Nachweis der Person ansehen',
    body: 'Jeder Checkpoint, den der Server von EngineerOS für dieses Konto bestätigt hat, auf dem heutigen Stand.',
  },
  provenance: 'EngineerOS stellt dieses Zertifikat anhand der Aufzeichnungen seines eigenen Bewertungsservers aus. Keine andere Stelle hat es geprüft oder akkreditiert.',
  missing: {
    title: 'Kein solches Zertifikat',
    body: 'EngineerOS hat kein Zertifikat mit dieser ID. Vielleicht wurde sie falsch abgetippt, oder das Konto, zu dem es gehörte, wurde geschlossen.',
    asked: 'Die ID, die du geöffnet hast',
  },
  cta: 'Zum Kurs',
  ctaWhy: 'EngineerOS lehrt Softwareentwicklung, indem man KI-Agenten anleitet, auf einer echten Linux-Maschine im Browser-Tab. Seine Checkpoints werden auf einem Server bewertet, und seine Zertifikate werden auf Grundlage dieser Bewertungen ausgestellt.',
};

export const ja: typeof en = {
  tab: (holder: string, name: string) => `${holder} · 修了証: ${name} · EngineerOS`,
  tabRevoked: (holder: string, name: string) => `${holder} · 修了証: ${name}(無効) · EngineerOS`,
  tabMissing: '該当する修了証がありません · EngineerOS',
  label: '修了証',
  loading: '確認しています…',
  issuedTo: '発行先',
  issued: '発行日',
  valid: '有効',
  validBody: 'この修了証はEngineerOSが発行したもので、取り消されていません。',
  revoked: {
    state: '無効',
    since: (date: string) => `${date}から無効`,
    body: 'EngineerOSはこの修了証を取り消しました。この修了証も、その根拠となったチェックポイントも、もう保証しません。すでに共有されたリンクが消えてしまうのではなく、その旨が伝わるよう、このページは残されています。',
  },
  checkpoints: {
    label: '根拠となるチェックポイント',
    on: (date: string) => `${date}に合格`,
    graded: '採点バージョン',
  },
  how: {
    label: '読み方',
    server: {
      term: 'サーバーによる採点',
      body: 'チェックポイントはどれも、提出されたリポジトリを対象に、EngineerOSのサーバーで採点されました。保持者自身のブラウザーによる採点ではありません。ブラウザーは、合格になる内容を報告できないからです。',
    },
    own: {
      term: '専用のリポジトリ',
      body: 'チェックポイントはどれも、このアカウントだけに発行されたリポジトリから始まります。そのため、別のアカウントから完成済みのワークスペースをコピーしても合格しません。',
    },
    limit: {
      term: '証明しないこと',
      body: 'どのアカウントが合格したかを示すもので、誰がキーボードを操作していたかは示しません。EngineerOSでは、入力はエージェントが行い、保持者が指示と判断を担うこともあります。採点では成績、スコア、所要時間を測っていないため、これらは記載されません。',
    },
  },
  id: {
    label: '修了証ID',
    body: 'このIDを知っている人は誰でもこのページを開けます。表示されるのは、修了証について現時点で正しい内容です。',
  },
  profile: {
    label: '公開記録',
    link: '保持者の公開記録を見る',
    body: 'このアカウントについてEngineerOSのサーバーが確認したすべてのチェックポイントを、現時点の状態で表示します。',
  },
  provenance: 'この修了証は、EngineerOS自身の採点サーバーの記録にもとづいて発行されています。ほかの機関による審査や認定は受けていません。',
  missing: {
    title: '該当する修了証がありません',
    body: 'このIDの修了証はEngineerOSにありません。IDの入力ミスか、修了証を持っていたアカウントが閉鎖された可能性があります。',
    asked: '開いたID',
  },
  cta: 'コースを見る',
  ctaWhy: 'EngineerOSは、ブラウザーのタブの中の本物のLinuxマシンで、AIエージェントに指示を出しながらソフトウェアエンジニアリングを学ぶコースです。チェックポイントはサーバーで採点され、修了証はその採点結果にもとづいて発行されます。',
};
